// GLB-01: ⌘K / Ctrl+K. Navigate, search help, or open a record by ID.
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Dialog, Input, ListBox, ListBoxItem, Modal, ModalOverlay, SearchField, Header, ListBoxSection, Collection } from "react-aria-components";
import { searchHelp, type HelpHit } from "./help";
import { useCapabilities } from "./capabilities";
import { visibleNav } from "./nav";

const ID_ROUTES: [RegExp, (id: string) => string][] = [
  [/^usr_[0-9A-Z]{26}$/i, (id) => `/admin/users/${id}`],
  [/^app_[a-z][a-z0-9-]+$/, (id) => `/dev/apps/${id}`],
  [/^key_[0-9A-Z]{26}$/i, (id) => `/dev/keys/${id}`],
  [/^whk_[0-9A-Z]{26}$/i, (id) => `/dev/webhooks/${id}`],
  [/^evt_[0-9A-Z]{26}$/i, (id) => `/admin/audit/${id}`],
  [/^ent_[0-9A-Z]{26}$/i, (id) => `/admin/entitlements/${id}`],
  [/^role_[a-z0-9_]+$/, (id) => `/admin/roles/${id}`],
  [/^org_[0-9A-Z]{26}$/i, (id) => `/orgs/${id}/members`],
];

interface Item {
  id: string;
  label: string;
  hint: string;
  to: string;
}

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate();
  const { data: caps } = useCapabilities();
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<HelpHit[]>([]);

  useEffect(() => {
    let live = true;
    searchHelp(query, 8)
      .then((h) => live && setHits(h))
      .catch(() => live && setHits([]));
    return () => {
      live = false;
    };
  }, [query]);

  const sections = useMemo(() => {
    const q = query.trim().toLowerCase();
    const idMatch = ID_ROUTES.find(([re]) => re.test(query.trim()));
    const nav: Item[] = visibleNav(caps)
      .flatMap((s) => s.items)
      .filter((i) => !q || i.label.toLowerCase().includes(q))
      .map((i) => ({ id: `nav:${i.to}`, label: i.label, hint: i.external ? "Opens the help center" : "Go to", to: i.to }));
    const help: Item[] = hits.map((h) => ({ id: `help:${h.url}`, label: h.title, hint: `Help · ${h.kind}`, to: h.url }));
    const out: { name: string; items: Item[] }[] = [];
    if (idMatch) out.push({ name: "Open", items: [{ id: "open-id", label: query.trim(), hint: "Open by ID", to: idMatch[1](query.trim()) }] });
    if (nav.length) out.push({ name: "Go to", items: nav.slice(0, 8) });
    if (help.length) out.push({ name: "Help", items: help });
    return out;
  }, [query, hits, caps]);

  function go(key: React.Key) {
    const item = sections.flatMap((s) => s.items).find((i) => i.id === key);
    if (!item) return;
    onOpenChange(false);
    setQuery("");
    if (/^https?:/.test(item.to)) {
      window.open(item.to, "_blank", "noopener");
      return;
    }
    const [path, hash] = item.to.split("#");
    void navigate({ to: path!, hash });
  }

  return (
    <ModalOverlay isOpen={open} onOpenChange={onOpenChange} isDismissable className="modal-overlay">
      <Modal className="modal palette">
        <Dialog aria-label="Command palette">
          <SearchField aria-label="Search" value={query} onChange={setQuery} autoFocus className="palette-search">
            <Input placeholder="Search screens, help, or paste an ID" />
          </SearchField>
          <ListBox aria-label="Results" className="palette-results" onAction={go} items={sections} renderEmptyState={() => <p className="muted pad">No matches.</p>}>
            {(section) => (
              <ListBoxSection id={section.name}>
                <Header>{section.name}</Header>
                <Collection items={section.items}>
                  {(item) => (
                    <ListBoxItem id={item.id} textValue={item.label} className="palette-item">
                      <span>{item.label}</span>
                      <span className="muted">{item.hint}</span>
                    </ListBoxItem>
                  )}
                </Collection>
              </ListBoxSection>
            )}
          </ListBox>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
